import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { getDashboard, getMilkSummary, getProductions, getMilkBatches, getTrends, getOrders, getPlans, getReceptionPlans, getConsumptionTrend, getLossTrend, fmt } from "@/lib/api";
import { appPath } from "@/lib/paths";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { SupplierTrend } from "@/components/SupplierTrend";
import { formatDate } from "@/lib/dates";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DashboardCharts, ConsumptionTrendChart, LossTrendChart } from "./dashboard/Charts";
import { SalesTrend } from "./dashboard/SalesTrend";
import {
  Droplets,
  FlaskConical,
  AlertTriangle,
  ShieldCheck,
  CalendarDays,
  CalendarClock,
  Snowflake,
  Target,
  Plus,
  ArrowRight,
  ShoppingCart,
  PackageCheck,
  Truck,
} from "lucide-react";

const EXPIRY_WINDOW_DAYS = 7;

const Kpi = ({ icon: Icon, label, value, unit, tone = "brand", testid }) => {
  const toneCls = {
    brand: "text-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.10)]",
    success: "text-[hsl(var(--success))] bg-[hsl(var(--qc-ok-bg))]",
    warning: "text-[hsl(var(--warning))] bg-[hsl(var(--qc-warn-bg))]",
    danger: "text-[hsl(var(--danger))] bg-[hsl(var(--qc-bad-bg))]",
  }[tone];
  return (
    <Card data-testid={testid} className="overflow-hidden">
      <CardContent className="flex items-center gap-4 p-5">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${toneCls}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold font-mono tabular-nums leading-none">
            {value}
            {unit ? <span className="ml-1 text-sm font-normal text-muted-foreground">{unit}</span> : null}
          </div>
          <div className="mt-1 truncate text-sm text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const MilkAvg = ({ label, value, suffix = "", decimals = 2, testid }) => (
  <div data-testid={testid}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-mono text-xl font-semibold tabular-nums leading-tight">
      {value === null || value === undefined ? "—" : `${fmt(value, decimals)}${suffix}`}
    </div>
  </div>
);


// Inclusive YYYY-MM-DD date window for the "Total milk received" card period filter.
// Milk batch dates are stored as local YYYY-MM-DD, so we build the range in local time.
function milkRange(periodKey, monthStr) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  const pad = (n) => String(n).padStart(2, "0");
  const fmtD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (periodKey === "all") return {};
  if (periodKey === "this_year") return { start: `${y}-01-01`, end: `${y}-12-31` };
  if (periodKey === "last_month") {
    return { start: fmtD(new Date(y, m - 1, 1)), end: fmtD(new Date(y, m, 0)) };
  }
  if (periodKey === "custom" && monthStr) {
    const [yy, mm] = monthStr.split("-").map(Number);
    return { start: fmtD(new Date(yy, mm - 1, 1)), end: fmtD(new Date(yy, mm, 0)) };
  }
  // this_month (default) — resets automatically at the start of each calendar month.
  return { start: fmtD(new Date(y, m, 1)), end: fmtD(new Date(y, m + 1, 0)) };
}


export default function Dashboard() {
  const navigate = useNavigate();
  const { t, td } = useT();
  const { user, canAccess } = useAuth();
  const [stats, setStats] = useState(null);
  const [productions, setProductions] = useState([]);
  const [milk, setMilk] = useState([]);
  const [trends, setTrends] = useState(null);
  const [period, setPeriod] = useState("current_month");
  const [orderCounts, setOrderCounts] = useState({ open: 0, completed: 0, total: 0 });
  const [planning, setPlanning] = useState([]);
  const [milkPeriod, setMilkPeriod] = useState("this_month");
  const [milkMonth, setMilkMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM (custom)
  const [milkSummary, setMilkSummary] = useState(null);
  const [scTrend, setScTrend] = useState(null);
  const [lossTrend, setLossTrend] = useState(null);
  const isManager = user?.role === "admin" || user?.role === "manager";

  // Specific-consumption + loss trend charts are management KPIs — admin/manager only.
  useEffect(() => {
    if (!isManager) return;
    (async () => {
      try { setScTrend(await getConsumptionTrend({ months: 6 })); } catch { setScTrend({ months: [], series: [] }); }
      try { setLossTrend(await getLossTrend({ months: 6 })); } catch { setLossTrend({ months: [], reasons: [], data: [], total_kg: 0 }); }
    })();
  }, [isManager]);

  const loadPlanning = async () => {
    try {
      const [pl, rp] = await Promise.all([
        getPlans().catch(() => []),
        getReceptionPlans().catch(() => []),
      ]);
      const rows = [
        ...(pl || [])
          .filter((p) => p.status === "planned")
          .map((p) => ({
            id: `plan-${p.id}`,
            kind: "plan",
            date: p.planned_date,
            detail: p.product_type,
            volume: p.planned_volume_l,
            info: p.operator_name || null,
          })),
        ...(rp || [])
          .filter((r) => r.status === "scheduled")
          .map((r) => ({
            id: `rec-${r.id}`,
            kind: "reception",
            date: r.expected_date,
            detail: r.supplier,
            volume: r.expected_volume_l,
            info: r.expected_fat != null ? `${r.expected_fat}%` : null,
          })),
      ]
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
        .slice(0, 8);
      setPlanning(rows);
    } catch { /* non-fatal */ }
  };

  const loadOrderCounts = async () => {
    try {
      const all = await getOrders();
      const open = (all || []).filter((o) => o.status === "placed" || o.status === "confirmed").length;
      const completed = (all || []).filter((o) => o.status === "delivered").length;
      setOrderCounts({ open, completed, total: (all || []).length });
    } catch { /* non-fatal */ }
  };

  const load = async () => {
    // Resilient to section-permission (403) restrictions: each source is optional so the
    // dashboard still renders whatever the current role is allowed to see.
    const [s, p, m] = await Promise.all([
      getDashboard().catch(() => null),
      getProductions().catch(() => []),
      getMilkBatches().catch(() => []),
    ]);
    if (s) setStats(s);
    setProductions(p || []);
    setMilk(m || []);
    loadOrderCounts();
    loadPlanning();
  };
  useEffect(() => {
    (async () => { await load(); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    (async () => {
      try { setTrends(await getTrends(period)); } catch { setTrends(null); }
    })();
  }, [period]);
  // Load the milk-received summary for the selected period (defaults to the current month).
  useEffect(() => {
    (async () => {
      try { setMilkSummary(await getMilkSummary(milkRange(milkPeriod, milkMonth))); }
      catch { setMilkSummary(null); }
    })();
  }, [milkPeriod, milkMonth]);

  const exceptions = useMemo(
    () => productions.flatMap((p) => (p.qc_warnings || []).map((w) => ({ ...w, prod: p }))).slice(0, 8),
    [productions]
  );

  // Products approaching their shelf-life limit (within 7 days, including expired)
  // Expiring soon = stock lots still IN STOCK (remaining > 0) that expire within the window.
  // Computed on the backend from stock_lots and returned via /dashboard.
  const expiringSoon = useMemo(
    () => (stats?.expiring_soon || []).map((l) => ({ ...l, daysLeft: l.days_left })).slice(0, 8),
    [stats],
  );

  const expiryTone = (d) =>
    d < 0
      ? "badge-bad"
      : d <= 2
      ? "bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]"
      : "bg-[hsl(var(--brand)/0.08)] text-[hsl(var(--brand))]";
  const expiryLabel = (d) =>
    d < 0 ? t("dashboard.expiredAgo", { n: Math.abs(d) }) : d === 0 ? t("dashboard.expiresToday") : t("dashboard.daysLeft", { n: d });

  // Productions in the maturation stage that are ready (or nearly ready) to move to the cold room
  const coldRoom = useMemo(
    () => productions
      .filter((p) => p.in_maturation && p.maturation_ready_date && p.maturation_days_left != null && p.maturation_days_left <= EXPIRY_WINDOW_DAYS)
      .sort((a, b) => a.maturation_days_left - b.maturation_days_left)
      .slice(0, 8),
    [productions]
  );
  const coldRoomLabel = (d) =>
    d < 0 ? t("dashboard.readyAgo", { n: Math.abs(d) }) : d === 0 ? t("dashboard.readyToday") : t("dashboard.daysLeft", { n: d });
  const coldRoomTone = (d) =>
    d <= 0
      ? "badge-bad"
      : d <= 2
      ? "bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]"
      : "bg-[hsl(var(--brand)/0.08)] text-[hsl(var(--brand))]";

  // Yield vs target efficiency, averaged per product type (only products with a target + at least one yield)
  const yieldByProduct = useMemo(
    () => Object.values(
      productions.reduce((acc, p) => {
        if (!p.target_yield_per_100l || p.yield_efficiency_pct == null) return acc;
        const k = p.product_type;
        acc[k] = acc[k] || { product: k, target: p.target_yield_per_100l, effSum: 0, actualSum: 0, runs: 0 };
        acc[k].effSum += p.yield_efficiency_pct;
        acc[k].actualSum += p.actual_yield_per_100l || 0;
        acc[k].runs += 1;
        return acc;
      }, {})
    )
      .map((m) => ({ ...m, avgEff: Math.round(m.effSum / m.runs), avgActual: m.actualSum / m.runs }))
      .sort((a, b) => a.avgEff - b.avgEff),
    [productions]
  );
  const effTone = (pct) => (pct >= 95 ? "badge-ok" : pct >= 80 ? "bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]" : "badge-bad");
  const effBar = (pct) => (pct >= 95 ? "bg-[hsl(var(--success))]" : pct >= 80 ? "bg-[hsl(var(--warning))]" : "bg-[hsl(var(--danger))]");

  return (
    <Layout
      actions={
        <>
          <Button variant="outline" size="sm" data-testid="dash-add-milk" onClick={() => navigate(appPath("/milk"))}>
            <Plus className="mr-1.5 h-4 w-4" /> {t("dashboard.milkBtn")}
          </Button>
          <Button size="sm" data-testid="dash-add-production" onClick={() => navigate(appPath("/productions"))}>
            <Plus className="mr-1.5 h-4 w-4" /> {t("dashboard.productionBtn")}
          </Button>
        </>
      }
    >
      <div className="header-band -mx-3 -mt-4 mb-5 rounded-b-2xl px-3 py-5 sm:-mx-6 sm:-mt-6 sm:mb-6 sm:px-6 sm:py-6 lg:-mx-8 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">{t("dashboard.overviewTitle")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("dashboard.overviewSubtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t("dashboard.chartsPeriod")}</span>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-9 w-[150px] bg-card" data-testid="dash-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="current_month">{t("dashboard.currentMonth")}</SelectItem>
                <SelectItem value="last_month">{t("dashboard.lastMonth")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <Kpi icon={Droplets} label={t("dashboard.milkAvailable")} value={fmt(stats?.total_remaining_l)} unit="L" tone="brand" testid="kpi-milk" />
        <Kpi icon={FlaskConical} label={t("dashboard.activeProductions")} value={stats?.in_progress_productions ?? "-"} tone="success" testid="kpi-productions" />
        <Kpi icon={CalendarDays} label={t("dashboard.planned")} value={(stats?.planned_productions || 0) + (stats?.planned_receptions || 0)} tone="brand" testid="kpi-planned" />
        <Kpi icon={AlertTriangle} label={t("dashboard.qcExceptions")} value={stats?.qc_exceptions ?? "-"} tone="warning" testid="kpi-exceptions" />
        <Kpi icon={ShieldCheck} label={t("dashboard.haccpAlerts")} value={(stats?.open_haccp_alerts || 0) + (stats?.overdue_ccps || 0)} tone={(stats?.open_haccp_alerts || stats?.overdue_ccps) ? "danger" : "brand"} testid="kpi-haccp" />
      </div>

      {/* Total milk received + volume-weighted averages (period-filtered, defaults to this month) */}
      <Card className="mt-6" data-testid="dash-milk-received">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Droplets className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("dashboard.milkReceived")}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={milkPeriod} onValueChange={setMilkPeriod}>
                <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="dash-milk-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">{t("dashboard.periodThisMonth")}</SelectItem>
                  <SelectItem value="last_month">{t("dashboard.periodLastMonth")}</SelectItem>
                  <SelectItem value="this_year">{t("dashboard.periodThisYear")}</SelectItem>
                  <SelectItem value="all">{t("dashboard.periodAllTime")}</SelectItem>
                  <SelectItem value="custom">{t("dashboard.periodCustomMonth")}</SelectItem>
                </SelectContent>
              </Select>
              {milkPeriod === "custom" && (
                <input
                  type="month"
                  value={milkMonth}
                  max={new Date().toISOString().slice(0, 7)}
                  onChange={(e) => setMilkMonth(e.target.value)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  data-testid="dash-milk-month"
                />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
            <div>
              <div className="text-xs text-muted-foreground">
                {t("milk.totalIntake")}
                {milkSummary?.batches != null && (
                  <span className="ml-1.5" data-testid="dash-milk-batches">· {t("milk.acrossBatches", { n: milkSummary.batches })}</span>
                )}
              </div>
              <div className="font-mono text-3xl font-semibold tabular-nums leading-tight" data-testid="dash-milk-total">
                {fmt(milkSummary?.total_intake_l)} <span className="text-lg font-normal text-muted-foreground">L</span>
              </div>
            </div>
            <div className="hidden h-10 w-px bg-border sm:block" />
            <MilkAvg label={t("milk.avgFat")} value={milkSummary?.avg_fat} suffix="%" decimals={2} testid="dash-avg-fat" />
            <MilkAvg label={t("milk.avgProtein")} value={milkSummary?.avg_protein} suffix="%" decimals={2} testid="dash-avg-protein" />
            <MilkAvg label={t("milk.avgDensity")} value={milkSummary?.avg_density} suffix="" decimals={3} testid="dash-avg-density" />
          </div>
        </CardContent>
      </Card>

      {/* Supplier quality trend — who is slipping on fat / protein / temperature */}
      {canAccess("milk") && (
        <div className="mt-6">
          <SupplierTrend compact limit={5} />
        </div>
      )}

      {/* Orders — open vs completed */}
      <div className="mt-6 grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2" data-testid="dash-orders-summary">
        <Card className="overflow-hidden cursor-pointer transition-colors hover:bg-secondary/40" onClick={() => navigate(appPath("/orders"))} data-testid="kpi-orders-open">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]">
              <ShoppingCart className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="text-2xl font-semibold font-mono tabular-nums leading-none">{orderCounts.open}</div>
              <div className="mt-1 truncate text-sm text-muted-foreground">{t("dashboard.openOrders")}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden cursor-pointer transition-colors hover:bg-secondary/40" onClick={() => navigate(appPath("/delivery-notes"))} data-testid="kpi-orders-completed">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--qc-ok-bg))] text-[hsl(var(--success))]">
              <PackageCheck className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="text-2xl font-semibold font-mono tabular-nums leading-none">{orderCounts.completed}</div>
              <div className="mt-1 truncate text-sm text-muted-foreground">{t("dashboard.completedOrders")}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming planning (production plans + milk receptions) */}
      <Card className="mt-6" data-testid="dash-planning-table">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("dashboard.planningTitle")}
            {planning.length > 0 && (
              <span className="rounded-full bg-[hsl(var(--qc-warn-bg))] px-2 py-0.5 text-xs font-medium text-[hsl(var(--warning))]" data-testid="dash-planning-count">
                {planning.length}
              </span>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate(appPath("/planning"))} data-testid="dash-view-planning">
            {t("dashboard.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {planning.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="dash-planning-empty">
              {t("dashboard.noPlanning")}
            </p>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("dashboard.planColDate")}</TableHead>
                    <TableHead>{t("dashboard.planColType")}</TableHead>
                    <TableHead>{t("dashboard.planColDetail")}</TableHead>
                    <TableHead className="text-right">{t("dashboard.planColVolume")}</TableHead>
                    <TableHead>{t("dashboard.planColInfo")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {planning.map((row) => (
                    <TableRow key={row.id} data-testid={`dash-planning-row-${row.id}`}>
                      <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">{formatDate(row.date)}</TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={`gap-1 text-[10px] ${row.kind === "reception" ? "bg-[hsl(var(--accent)/0.18)] text-[hsl(var(--brand))]" : "badge-warn"}`}
                        >
                          {row.kind === "reception" ? <Truck className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
                          {row.kind === "reception" ? t("planning.milkReception") : t("planning.productionPlan")}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{row.kind === "reception" ? row.detail : td(row.detail)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                        {row.volume != null ? `${fmt(row.volume)} L` : "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.info || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trend charts */}
      <DashboardCharts trends={trends} />
      {isManager && <ConsumptionTrendChart trend={scTrend} />}
      {isManager && <LossTrendChart trend={lossTrend} />}

      {/* Sales value trend (delivered orders) — admin only */}
      {user?.role === "admin" && <SalesTrend />}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{t("dashboard.recentProductions")}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(appPath("/productions"))} data-testid="dash-view-productions">
              {t("dashboard.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {productions.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">{t("dashboard.noProductions")}</p>
            )}
            {productions.slice(0, 6).map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-muted-foreground">{p.production_code}</div>
                  <div className="truncate text-sm font-medium">{td(p.product_type)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm tabular-nums text-muted-foreground">{fmt(p.total_milk_l)} L</span>
                  <StatusBadge status={(p.qc_warnings || []).length ? "warn" : "ok"} label={(p.qc_warnings || []).length ? `${p.qc_warnings.length} ${t("dashboard.flag")}` : "OK"} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.qcExceptionsTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {exceptions.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">{t("dashboard.noOutOfRange")}</p>
            )}
            {exceptions.map((e, i) => (
              <div key={`${e.prod?.id || e.prod?.production_code}-${e.field}-${i}`} className="flex items-center justify-between rounded-lg badge-bad px-3 py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{e.field}</div>
                  <div className="text-xs opacity-80">{td(e.prod.product_type)} · {e.prod.production_code}</div>
                </div>
                <div className="font-mono text-sm">{fmt(e.value, 2)} — {e.issue}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6" data-testid="expiring-soon-widget">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("dashboard.expiringSoon")}
            {expiringSoon.length > 0 && (
              <span className="rounded-full bg-[hsl(var(--qc-warn-bg))] px-2 py-0.5 text-xs font-medium text-[hsl(var(--warning))]" data-testid="expiring-soon-count">
                {expiringSoon.length}
              </span>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate(appPath("/stock"))} data-testid="dash-view-expiring">
            {t("dashboard.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {expiringSoon.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="expiring-soon-empty">
              {t("dashboard.noExpiring", { days: EXPIRY_WINDOW_DAYS })}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {expiringSoon.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5"
                  data-testid={`expiring-item-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-muted-foreground">{p.lot_code}</div>
                    <div className="truncate text-sm font-medium">{td(p.product_type)} <span className="text-xs font-normal text-muted-foreground">· {fmt(p.remaining)} {p.unit}</span></div>
                    <div className="font-mono text-xs text-muted-foreground">{t("dashboard.exp")} {formatDate(p.expiry_date)}</div>
                  </div>
                  <span className={`whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${expiryTone(p.daysLeft)}`} data-testid={`expiring-badge-${p.id}`}>
                    {expiryLabel(p.daysLeft)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6" data-testid="cold-room-widget">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Snowflake className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("dashboard.readyForColdRoom")}
            {coldRoom.length > 0 && (
              <span className="rounded-full bg-[hsl(var(--qc-warn-bg))] px-2 py-0.5 text-xs font-medium text-[hsl(var(--warning))]" data-testid="cold-room-count">
                {coldRoom.length}
              </span>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate(appPath("/productions?maturation=1"))} data-testid="dash-view-cold-room">
            {t("dashboard.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {coldRoom.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="cold-room-empty">
              {t("dashboard.noColdRoom", { days: EXPIRY_WINDOW_DAYS })}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {coldRoom.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5"
                  data-testid={`cold-room-item-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-muted-foreground">{p.production_code}</div>
                    <div className="truncate text-sm font-medium">{td(p.product_type)}</div>
                    <div className="font-mono text-xs text-muted-foreground">{t("dashboard.ready")} {p.maturation_ready_date}</div>
                  </div>
                  <span className={`whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${coldRoomTone(p.maturation_days_left)}`} data-testid={`cold-room-badge-${p.id}`}>
                    {coldRoomLabel(p.maturation_days_left)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6" data-testid="yield-efficiency-widget">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Target className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("dashboard.yieldEfficiency")}
          </CardTitle>
          <p className="text-xs text-muted-foreground">{t("dashboard.yieldSubtitle")}</p>
        </CardHeader>
        <CardContent>
          {yieldByProduct.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="yield-efficiency-empty">
              {t("dashboard.noYieldData")}
            </p>
          ) : (
            <div className="space-y-3">
              {yieldByProduct.map((m) => (
                <div key={m.product} className="space-y-1.5" data-testid={`yield-efficiency-row-${m.product}`}>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium">{td(m.product)}</span>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-mono">{fmt(m.avgActual, 1)} / {fmt(m.target, 1)} kg/100L</span>
                      <span className="text-muted-foreground/60">·</span>
                      <span>{m.runs} {m.runs > 1 ? t("dashboard.runs") : t("dashboard.run")}</span>
                      <span className={`rounded-full px-2 py-0.5 font-medium ${effTone(m.avgEff)}`} data-testid={`yield-efficiency-badge-${m.product}`}>
                        {m.avgEff}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-secondary">
                    <div className={`h-full rounded-full ${effBar(m.avgEff)}`} style={{ width: `${Math.min(m.avgEff, 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
